import React, { useEffect, useState } from "react";
import {
  Drawer,
  Form,
  Input,
  Button,
  Select,
  DatePicker,
  message,
  Row,
  Col,
} from "antd";
import moment from 'moment';
import 'moment-timezone';
import API from "../../api";

const { Option } = Select;
const { TextArea } = Input;

const DiscountForm = ({ open, setOpen, initialValue, isEdit, onSubmit }) => {
  const [form] = Form.useForm();
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loadingBrands, setLoadingBrands] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [validFrom,setValidFrom] = useState(null)
  const [validUntill,setValidUntill] = useState(null)

  // Fetch Brands
  const fetchBrands = async () => {
    setLoadingBrands(true);
    try {
      const response = await API.get("/brands");
      setBrands(response?.data || []);
    } catch (error) {
      console.error("Failed to fetch brands:", error);
      message.error("Unable to load brands.");
    } finally {
      setLoadingBrands(false);
    }
  };

  // Fetch Categories
  const fetchCategories = async () => {
    setLoadingCategories(true);
    try {
      const response = await API.get("/category");
      setCategories(response?.data || []);
    } catch (error) {
      console.error("Failed to fetch categories:", error);
      message.error("Unable to load categories.");
    } finally {
      setLoadingCategories(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchCategories();
      fetchBrands();
  
      if (isEdit && initialValue) {
        form.setFieldsValue({
          ...initialValue,
          applicableCategories: initialValue.applicableCategories
            ? initialValue.applicableCategories.map((cat) => cat?._id)
            : [],
          applicableBrands: initialValue.applicableBrands
            ? initialValue.applicableBrands.map((brand) => brand?._id)
            : [],
          validFrom: initialValue?.validFrom
            ? moment(initialValue?.validFrom).tz("Asia/Kolkata", true)
            : null,
          validUntil: initialValue?.validUntil
            ? moment(initialValue?.validUntil).tz("Asia/Kolkata", true)
            : null,
        });
      } else {
        form.resetFields();
      }
    }
  }, [open, isEdit, initialValue, form]);
  const onFinish = async (values) => {
    try {
      const formattedValues = {
        ...values,
        validFrom: validFrom
          ? moment(validFrom).tz("Asia/Kolkata", true).startOf("day").toISOString()
          : undefined,
        validUntil: validUntill
          ? moment(validUntill).tz("Asia/Kolkata", true).endOf("day").toISOString()
          : undefined,
      };
      await onSubmit(formattedValues);
      form.resetFields();
      setOpen(false);
    } catch (error) {
      console.error("Submit failed:", error);
      message.error(error.response?.data?.message);
    }
  };
  
  return (
    <Drawer
      title={isEdit ? "Edit Discount" : "Add Discount"}
      width={500}
      placement="right"
      onClose={() => setOpen(false)}
      open={open}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={initialValue}
      >
        <Form.Item
          label="Discount Name"
          name="name"
          rules={[{ required: true, message: "Please enter the discount name!" }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="Type"
          name="type"
          rules={[{ required: true, message: "Please select the discount type!" }]}
        >
          <Select
            showSearch
            placeholder="Select type"
            optionFilterProp="children"
            filterOption={(input, option) => {
              if (option?.children) {
                const text = option.children.toLowerCase();
                const inputValue = input.toLowerCase();
                return text.includes(inputValue);
              }
              return false;
            }}
          >
            <Option value="percentage">Percentage</Option>
            <Option value="flat">Flat</Option>
          </Select>
        </Form.Item>


        <Form.Item
          label="Value"
          name="value"
          rules={[{ required: true, message: "Please enter the discount value!" }]}
        >
          <Input type="number" min="0" />
        </Form.Item>

        {/* Date Fields */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Valid From"
              name="validFrom"
              rules={[{ required: true, message: "Please select the start date!" }]}
            >
              <DatePicker
                style={{ width: "100%" }}
                disabledDate={(current) =>
                  current && current.isBefore(moment().startOf("day"))
                }
                format="YYYY-MM-DD"
                onChange={(date)=>setValidFrom(date)}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Valid Until"
              name="validUntil"
              rules={[
                { required: true, message: "Please select the end date!" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const validFrom = getFieldValue("validFrom");
                    if (!value || (validFrom && validFrom.isAfter(value, "day"))) {
                      return Promise.reject(
                        new Error("'Valid Until' must be after 'Valid From'.")
                      );
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <DatePicker
                style={{ width: "100%" }}
                disabledDate={(current) =>
                  current && current.isBefore(moment().startOf("day"))
                }
                format="YYYY-MM-DD"
                onChange={(date)=>setValidUntill(date)}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Categories Dropdown */}
        <Form.Item
          label="Categories"
          name="applicableCategories"
          rules={[{ required: true, message: "Please select at least one category!" }]}
        >
          <Select
            mode="multiple"
            placeholder="Select category"
            showSearch
            loading={loadingCategories}
            optionFilterProp="data-text"
            filterOption={(input, option) => {
              if (option?.props["data-text"]) {
                const searchValue = input?.toLowerCase() || "";
                const text = option.props["data-text"]?.toLowerCase() || "";
                return text.includes(searchValue);
              }
              return false;
            }}
          >
            {categories.map((category) => (
              <Option
                key={category?._id}
                value={category?._id}
                data-text={`${category?.name} ${category.parentCategory?.name ? `(${category?.parentCategory?.name})` : ""}`}
              >
                {category?.name}
                {category.parentCategory?.name ? ` (${category?.parentCategory?.name})` : ""}
              </Option>
            ))}
          </Select>
        </Form.Item>

        {/* Brands Dropdown */}
        <Form.Item
          label="Brands"
          name="applicableBrands"
          rules={[{ required: true, message: "Please select at least one brand!" }]}
        >
          <Select
            mode="multiple"
            placeholder="Select brand"
            showSearch
            loading={loadingBrands}
            optionFilterProp="children"
            filterOption={(input, option) => {
              if (option?.children) {
                const searchValue = input?.toLowerCase() || "";
                const text = option.children.toLowerCase() || "";
                return text.includes(searchValue);
              }
              return false;
            }}
          >
            {brands.map((brandItem) => (
              <Option key={brandItem?._id} value={brandItem?._id}>
                {brandItem?.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label="Terms and Conditions"
          name="termsAndConditions"
          rules={[{ required: true, message: "Please enter the terms and conditions!" }]}
        >
          <TextArea rows={4} />
        </Form.Item>

        <Form.Item>
          <Button type="primary" htmlType="submit" block>
            Save
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default DiscountForm;
